// Core lib dependencies.
import _forEach from 'lodash/forEach';

/**
 * <pre>
 * Storage service.
 *
 * *** NOTE ***
 * PRIVACY MANAGEMENT - if you are adding a usage of the storage service, BE SURE to check privacy
 * consents for cookies prior to usage, and provide fallback behavior there if consent is not given.
 * ************
 *
 * Summary of responsibilities:
 *
 *  - provides abstraction layer for storing temporary client side data (using localstorage and cookies).
 * </pre>
 *
 * @author Various
 * @namespace Common.Services.StorageService
 */
const StorageService = (function() {
  'use strict';

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  /**
   * Init.
   *
   * @memberOf Common.Services.StorageService
   */
  const init = () => {};

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Localstorage.
  //
  // *** SEE NOTE AT TOP OF FILE ***
  //
  const _localStorage = {
    // Local storage methods, only if localStorage is supported by the browser
    supported: function() {
      try {
        return typeof localStorage !== 'undefined';
      } catch (exc) {
        return false;
      }
    },
    getData: function(key) {
      if (this.supported()) return localStorage.getItem(key) ? localStorage.getItem(key) : false;
    },

    setData: function(key, value) {
      try {
        if (this.supported()) localStorage.setItem(key, value);
      } catch (err) {
        //console.log(err) // TODO: Currently silent errors
      }
    },

    // Add data to end of array
    pushData: function(key, value) {
      if (!this.supported()) return false;
      var _existing = JSON.parse(this.getData(key)),
        _values = _existing ? _existing : [];

      _values.push('' + value);

      this.setData(key, JSON.stringify(_values));
    },

    popData: function(key, value) {
      if (!this.supported()) return false;
      var _values = [],
        _existing = this.getData(key);

      if (_existing) {
        _forEach(_existing.split(','), function(el) {
          if ('' + el !== '' + value) _values.push('' + el);
        });
        this.setData(key, _values);
      }
    },

    clearData: function(key) {
      if (this.supported()) localStorage.removeItem(key);
    },

    clearByRegex: function(regex) {
      if (!(this.supported())) return;
      Object.keys(localStorage).forEach((key) => {
        if (regex.test(key)) {
          _localStorage.clearData(key);
        }
      });
    },
  };  // END local storage functions

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Cookie.
  //
  // *** SEE NOTE AT TOP OF FILE ***
  //
  const _cookieStorage = {
    // Cookie storage methods, used as fallback for non localStorage, longer lived and/or requests that need to go back to the server
    setData: function(key, value, expires) {
      var _key = key.replace(/\./g, '_'),
        _d = new Date(),
        _expires;

      // Use an overriding expiry Date if supplied.
      if (expires && expires instanceof Date) {
        _d = expires;
        // Otherwise default to 360 days from now.
      } else {
        _d.setTime(_d.getTime() + 360 * 24 * 60 * 60 * 1000);
      }

      _expires = 'expires=' + _d.toGMTString();

      // Add SameSite=None;Secure; here to allow saving cookies in embedded campaigns if the browser allows
      document.cookie = _key + '=' + value + '; ' + _expires + ';SameSite=None;Secure;path=/';
    },

    getData: function(key) {
      var _key = key.replace(/\./g, '_'),
        _cName = _key + '=',
        _cookies = document.cookie.split(';');

      for (var i = 0; i < _cookies.length; i++) {
        var c = _cookies[i];
        while (c.charAt(0) === ' ') c = c.substring(1);
        if (c.indexOf(_cName) !== -1) return c.substring(_cName.length, c.length);
      }

      return '';
    },

    /**
     * Cookie storage clearData performs a 'strong' delete of cookies, accounting for various permutations of the
     * domain (foo.bar.com & bar.com, etc).  You must match the original domain, and some of our cookies include
     * more or less of the domain.
     *
     * NOTE: If we ever stored cookies with a path other than '/', we'd need to iterate over each path part as well.
     *
     * @param {String} key the cookie name
     * @memberOf Common.Services.StorageService
     */
    clearData: function(key) {
      // Cookies use underscore anywhere that a period was used in a cookie name.
      const _key = key.replace(/\./g, '_');
      const _expires = '=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/';
      const cookieBase = _key + _expires;

      clearCookieAllDomainParts(cookieBase);
      // Some of our cookies use samesite / secure flags, so we need to try those too.
      clearCookieAllDomainParts(cookieBase + '; SameSite=None; Secure;');

      function clearCookieAllDomainParts(cookieBase) {
        // First delete without the domain
        document.cookie = cookieBase;

        // Then delete by all parts of the domain (foo.bar.com & bar.com, etc)
        const domain = window.location.hostname.split('.');
        while (domain.length > 0) {
          // If we ever stored with a path other than '/', we'd need to do each path part as well.
          const domainCookieBase = cookieBase + ' ;domain=' + domain.join('.');
          document.cookie = domainCookieBase;
          domain.shift();
        }
      }
    },

    clearByRegex: function(regex) {
      const cookies = document.cookie.split('; ');
      cookies.forEach(cookie => {
        const [cookieName, value] = cookie.split('=');
        if (regex.test(cookieName)) {
          clearData(cookieName);
        }
      });
    },
  };  // END Cookie functions

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Api Methods.

  const getStorageKey = function(components) {
    return components.join('_');
  };

  /**
   * Set the given key/value in either local storage, cookie or both
   *
   * *** SEE NOTE AT TOP OF FILE ***
   *
   * @memberOf Common.Services.StorageService
   *
   * @param key {string} Key for the value you wish to store.
   * @param value {mixed} Value to be store.
   */
  const setData = function(key, value, where, expires) {
    var _where = where || 'both';
    var _useLocal = _where == 'local' || _where == 'both',
      _useCookie = _where == 'cookie' || _where == 'both';

    try {
      if (_useLocal) _localStorage.setData(key, value);
      if (_useCookie) _cookieStorage.setData(key, value, expires);
    } catch (err) {
      //console.log(err) // TODO: currently Silent errors
    }
  };

  /**
   * Add data to end of array
   *
   * *** SEE NOTE AT TOP OF FILE ***
   *
   * @memberOf Common.Services.StorageService
   *
   * @param key {string} Key for the value you wish to append.
   * @param value {mixed} Value to be appended.
   */
  const pushData = function(key, value, where) {
    var _where = where || 'both';
    var _useLocal = _where == 'local' || _where == 'both',
      _useCookie = false; // (_where=="cookie"||_where=="both"); <-- Not currently supported [SG]

    if (_useLocal) _localStorage.pushData(key, value);
    if (_useCookie) _cookieStorage.pushData(key, value);
  };

  /**
   * Remove data from the end of array.
   *
   * *** SEE NOTE AT TOP OF FILE ***
   *
   * @memberOf Common.Services.StorageService
   *
   * @param key {string} Key for the value you wish to remove.
   * @param value {mixed} Value to be removed.
   */
  const popData = function(key, value, where) {
    var _where = where || 'both';
    var _useLocal = _where == 'local' || _where == 'both';
    _useCookie = false; // (_where=="cookie"||_where=="both"); <-- Not currently supported [SG]

    if (_useLocal) _localStorage.popData(key, value);
    if (_useCookie) _cookieStorage.popData(key, value);
  };

  /**
   * Get the given data value for key.
   *
   * *** SEE NOTE AT TOP OF FILE ***
   *
   * @memberOf Common.Services.StorageService
   *
   * @param key {string} Key for the value you wish to store.
   */
  const getData = function(key) {
    var _theData = _localStorage.getData(key);
    if (!_theData) {
      _theData = _cookieStorage.getData(key);
    }

    return _theData;
  };

  /**
   * Remove an item from local storage and cookie
   *
   * *** SEE NOTE AT TOP OF FILE ***
   *
   * @memberOf Common.Services.StorageService
   *
   * @param key {string} Key for the value you wish to clear.
   */
  const clearData = function(key) {
    if ('undefined' !== typeof key) {
      _localStorage.clearData(key);
      _cookieStorage.clearData(key);
      return true;
    }
  };

  /**
   * Remove an item from local storage and cookie by matching all keys to a regex
   *
   * *** SEE NOTE AT TOP OF FILE ***
   *
   * @memberOf Common.Services.StorageService
   *
   * @param key {RegExp} regex to match keys to
   */
  const clearDataByRegex = function(regex) {
    if (!regex) return;
    _cookieStorage.clearByRegex(regex);
    _localStorage.clearByRegex(regex);
  };

  //
  // *** SEE NOTE AT TOP OF FILE ***
  //
  // Expose scoped properties.
  return {
    init: init,

    get:          getData,
    set:          setData,
    push:         pushData,
    pop:          popData,
    clear:        clearData,
    clearByRegex: clearDataByRegex,
    getKey:       getStorageKey,
  };
})();

export default StorageService;
